import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { Button } from '@reflex/ui/components/ui/button';
import { asOrgId } from '@reflex/ui/client/api/org-id';
import { useDocumentTitle } from '@reflex/ui/hooks/useDocumentTitle';
import {
  useCreateShareRequestMutation,
  useShareRequests,
} from '@reflex/ui/client/api/share-requests';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useSessionSharingPolicy } from '@/hooks/auth/useSessionSharingPolicy';
import { useSocketMessage } from '@/hooks/socket/useSocket';
import { queryKeys } from '@/lib/query-keys';
import { isSessionSharingDisabled } from '@reflex/shared';
import type { WsServerMessage } from '@/lib/types';

export interface AgentNoAccessStateProps {
  agentId: string;
}

/**
 * Shown in place of the agent stream when the server answers the agent
 * read with a 403: the run exists in the caller's org but belongs to
 * another user and no grant covers it. Offers the ask side of sharing —
 * filing a share request the owner resolves from their own agent view —
 * and flips to the run automatically when the approval lands over WS.
 *
 * Titles the document like its sibling terminal state
 * (`AgentNotFoundState`): both replace the run on the same route, so a tab
 * left on one of them has to say which one it is.
 */
export function AgentNoAccessState({ agentId }: AgentNoAccessStateProps) {
  useDocumentTitle('No access to this session');
  const { user } = useAuth();
  const { activeOrganizationId } = useOrganization();
  const orgKey = useOrgKey();
  const queryClient = useQueryClient();
  const orgId = asOrgId(activeOrganizationId ?? 'global');
  // An org that allows no sharing has no approval that could land, and the
  // server refuses the ask; offering the button would only produce an error.
  const sharingDisabled = isSessionSharingDisabled(useSessionSharingPolicy());

  const { data } = useShareRequests(orgId, {
    resourceType: 'agent',
    resourceId: agentId,
    status: 'pending',
  });
  // An org admin sees every ask on this run; "already asked" means MY ask.
  const alreadyAsked = (data?.requests ?? []).some((r) => r.requesterUserId === user?.id);
  const createRequest = useCreateShareRequestMutation(orgId);

  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (msg.type !== 'share-request:resolved' || msg.resourceId !== agentId) return;
        void queryClient.invalidateQueries({ queryKey: ['share-requests', orgId] });
        if (msg.status === 'approved') {
          // The approval opened a grant; refetching the agent succeeds now
          // and the page swaps this screen for the run. The list caches
          // refetch too, so the sidebar picks the newly visible run up.
          void queryClient.invalidateQueries({ queryKey: [...queryKeys.agent(orgKey, agentId)] });
          void queryClient.invalidateQueries({ queryKey: ['agents', orgKey] });
          toast.success('Access granted', { description: 'The run was shared with you.' });
        } else {
          // Without this the pending state silently flips back to the ask
          // button, indistinguishable from a glitch.
          toast('Request declined', { description: 'The owner declined your request.' });
        }
      },
      [agentId, orgId, orgKey, queryClient],
    ),
  );

  async function handleAsk() {
    try {
      await createRequest.mutateAsync({
        data: { resourceType: 'agent', resourceId: agentId },
      });
      toast.success('Request sent', { description: 'The owner will see it on this run.' });
    } catch (err) {
      toast.error('Failed to send request', {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  }

  return (
    <EmptyState
      variant="fullHeight"
      icon={<Lock className="icon-md text-muted-foreground" />}
      title="You don't have access to this run"
      description={
        sharingDisabled
          ? 'This run belongs to another member of your organization, which does not allow sharing sessions.'
          : alreadyAsked
            ? 'Your request is waiting for the owner. You will get access here as soon as they share the run.'
            : 'This run belongs to another member of your organization. Ask its owner to share it with you.'
      }
      action={
        sharingDisabled ? null : alreadyAsked ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="icon-sm" />
            Request sent
          </div>
        ) : (
          <Button onClick={handleAsk} disabled={createRequest.isPending}>
            Ask to share
          </Button>
        )
      }
    />
  );
}
